import React, {useMemo} from "react";
import {
    Table, Thead, Tbody, Tr, Th, Td, Input, Button, HStack, Select, Text, Box, IconButton, TableContainer
} from "@chakra-ui/react";
import {FaTrash, FaUndo} from "react-icons/fa";
import {EditableRow, ResultRow, WEEKDAY_NAMES} from "./rowMapping";
import {formatHourString, getHourDecimals, getHourStep, parseEditableHour} from "./hourPrecision";
import {FavoriteEntry} from "./favorites";

export interface WorkingRow {
    key: string;
    jiraId: string;
    summary: string;
    comment: string;
    hoursRaw: string[];        // 7 raw editable strings
    approvedDays: boolean[];   // 7
    lockedDays: boolean[];     // 7 - copy-lock (existing target booking)
    locked: boolean;           // copy mode: row has at least one locked day
    fromApi: boolean;          // false = added via "+ Add favorite"
    blocked: boolean;          // restricted absence code, current user may not use
    deleted: boolean;          // edit mode: soft-deleted (booking to be zeroed)
}

let keySeq = 0;
function nextKey(): string {
    keySeq += 1;
    return `row-${keySeq}`;
}

export function buildWorkingRows(rows: EditableRow[]): WorkingRow[] {
    return rows.map((row) => ({
        key: nextKey(),
        jiraId: row.jiraId,
        summary: row.summary,
        comment: row.comment || "",
        hoursRaw: row.hours.map((h) => (h > 0 ? formatHourString(h) : "")),
        approvedDays: [...row.approvedDays],
        lockedDays: [...row.lockedDays],
        locked: row.locked,
        fromApi: row.fromApi,
        blocked: Boolean(row.blocked),
        deleted: false
    }));
}

export function makeFavoriteWorkingRow(fav: FavoriteEntry): WorkingRow {
    return {
        key: nextKey(),
        jiraId: fav.jiraId,
        summary: fav.summary,
        comment: fav.comment || "",
        hoursRaw: ["", "", "", "", "", "", ""],
        approvedDays: [false, false, false, false, false, false, false],
        lockedDays: [false, false, false, false, false, false, false],
        locked: false,
        fromApi: false,
        blocked: false,
        deleted: false
    };
}

function isCellImmutable(row: WorkingRow, mode: "copy" | "edit", day: number): boolean {
    if (row.blocked) { return true; }
    if (mode === "copy") { return Boolean(row.lockedDays[day]); }
    return Boolean(row.approvedDays[day]);
}

/** Row-level delete/restore affordance rules, mirrors script's showTrash logic. */
export function rowShowsTrash(row: WorkingRow, mode: "copy" | "edit"): boolean {
    if (mode === "copy") { return !row.locked; }
    const rowHasApproved = row.approvedDays.some(Boolean);
    return !rowHasApproved;
}

export interface WeekTotals {
    daySums: number[];
    weekSum: number;
    invalidCount: number;
}

export function computeTotals(rows: WorkingRow[]): WeekTotals {
    const daySums = [0, 0, 0, 0, 0, 0, 0];
    let invalidCount = 0;
    for (const row of rows.filter((r) => !r.deleted)) {
        row.hoursRaw.forEach((raw, day) => {
            const parsed = parseEditableHour(raw);
            if (parsed === null) { invalidCount += 1; return; }
            daySums[day] += parsed;
        });
    }
    const weekSum = daySums.reduce((a, b) => a + b, 0);
    return {daySums, weekSum, invalidCount};
}

/**
 * Converts working rows into the final submit payload. Active (non-deleted) rows
 * keep their edited hours; in edit mode, soft-deleted rows are emitted with all
 * hours forced to 0 so callers can zero out their existing bookings.
 * Returns null when any active cell fails validation.
 */
export function rowsToResult(rows: WorkingRow[], mode: "copy" | "edit"): ResultRow[] | null {
    const result: ResultRow[] = [];
    for (const row of rows) {
        if (row.deleted) {
            if (mode === "edit" && row.fromApi) {
                result.push({jiraId: row.jiraId, summary: row.summary, comment: row.comment, hours: [0, 0, 0, 0, 0, 0, 0], lockedDays: [false, false, false, false, false, false, false], fromApi: true});
            }
            continue;
        }
        const hours: number[] = [];
        for (const raw of row.hoursRaw) {
            const parsed = parseEditableHour(raw);
            if (parsed === null) { return null; }
            hours.push(parsed);
        }
        result.push({jiraId: row.jiraId, summary: row.summary, comment: row.comment, hours, lockedDays: row.lockedDays, fromApi: row.fromApi});
    }
    return result;
}

interface EditableWeekTableProps {
    rows: WorkingRow[];
    setRows: React.Dispatch<React.SetStateAction<WorkingRow[]>>;
    mode: "copy" | "edit";
    jiraBaseUrl: string;
    favorites: FavoriteEntry[];
    onManageFavorites: () => void;
}

export function EditableWeekTable({rows, setRows, mode, jiraBaseUrl, favorites, onManageFavorites}: EditableWeekTableProps) {
    const totals = useMemo(() => computeTotals(rows), [rows]);
    const decimals = getHourDecimals();
    const step = getHourStep();

    function updateHour(rowKey: string, day: number, value: string) {
        setRows((prev) => prev.map((r) => (r.key === rowKey ? {...r, hoursRaw: r.hoursRaw.map((h, i) => (i === day ? value : h))} : r)));
    }

    function updateComment(rowKey: string, value: string) {
        setRows((prev) => prev.map((r) => (r.key === rowKey ? {...r, comment: value} : r)));
    }

    function toggleDeleteRow(rowKey: string) {
        if (mode === "copy") {
            setRows((prev) => prev.filter((r) => r.key !== rowKey));
        } else {
            setRows((prev) => prev.map((r) => (r.key === rowKey ? {...r, deleted: !r.deleted} : r)));
        }
    }

    function addFavoriteRow(fav: FavoriteEntry) {
        const existing = rows.find((r) =>
            r.jiraId.toUpperCase() === fav.jiraId.toUpperCase()
            && r.summary.toLowerCase() === (fav.summary || "").toLowerCase()
            && r.comment.toLowerCase() === (fav.comment || "").toLowerCase());
        if (existing) {
            if (existing.deleted) { toggleDeleteRow(existing.key); }
            return;
        }
        setRows((prev) => [...prev, makeFavoriteWorkingRow(fav)]);
    }

    return (
        <Box>
            <HStack mb={2} flexWrap="wrap">
                <Text fontSize="sm" fontWeight={500}>Add favorite:</Text>
                {favorites.length === 0 ? (
                    <Text fontSize="xs" color="text-subtle" fontStyle="italic">No favorites configured. Click Manage to add some.</Text>
                ) : (
                    <Select
                        size="sm"
                        maxW="320px"
                        placeholder="-- choose a favorite --"
                        onChange={(e) => {
                            const idx = Number(e.target.value);
                            if (Number.isFinite(idx) && favorites[idx]) { addFavoriteRow(favorites[idx]); }
                            e.target.value = "";
                        }}
                    >
                        {favorites.map((fav, idx) => (
                            <option key={idx} value={idx}>
                                {[fav.jiraId, fav.summary, fav.comment].filter(Boolean).join(" — ")}
                            </option>
                        ))}
                    </Select>
                )}
                <Button size="sm" variant="outline" colorScheme="purple" onClick={onManageFavorites}>Manage...</Button>
            </HStack>

            <TableContainer overflowX="auto">
                <Table size="sm" variant="simple">
                    <Thead>
                        <Tr bg="surface-subtle">
                            <Th>Key</Th>
                            <Th>Summary</Th>
                            <Th>Comment</Th>
                            {WEEKDAY_NAMES.map((d) => <Th key={d} textAlign="center" isNumeric={false}>{d}</Th>)}
                            <Th textAlign="center">Del</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {rows.map((row) => {
                            const rowStyle = row.blocked
                                ? {borderLeft: "4px solid #dc2626", background: "#fef2f2", _dark: {background: "rgba(220, 38, 38, 0.15)"}}
                                : row.deleted ? {opacity: 0.5} : {};
                            const keyColor = row.blocked ? "var(--tb-key-blocked)" : "var(--tb-key-link)";
                            return (
                                <Tr key={row.key} sx={rowStyle}>
                                    <Td whiteSpace="nowrap" fontWeight={500}>
                                        <a href={jiraBaseUrl ? `${jiraBaseUrl}/browse/${row.jiraId}` : "#"} target="_blank" rel="noreferrer"
                                           style={{color: keyColor, textDecoration: row.deleted ? "line-through" : "none"}}
                                           title={row.blocked ? "Restricted absence code" : undefined}>
                                            {row.jiraId}
                                        </a>
                                    </Td>
                                    <Td style={{textDecoration: row.deleted ? "line-through" : "none"}}>{row.summary}</Td>
                                    <Td>
                                        <Input
                                            size="sm"
                                            placeholder="Comment for TimeBox"
                                            value={row.comment}
                                            isDisabled={row.locked || row.deleted}
                                            onChange={(e) => updateComment(row.key, e.target.value)}
                                        />
                                    </Td>
                                    {WEEKDAY_NAMES.map((_, day) => {
                                        const immutable = isCellImmutable(row, mode, day) || row.deleted;
                                        const raw = row.hoursRaw[day];
                                        const invalid = parseEditableHour(raw) === null;
                                        return (
                                            <Td key={day} textAlign="center" p={1}>
                                                <Input
                                                    size="sm"
                                                    type="number"
                                                    min={0}
                                                    max={24}
                                                    step={step}
                                                    width="64px"
                                                    textAlign="right"
                                                    value={raw}
                                                    isDisabled={immutable}
                                                    borderColor={invalid ? "red.400" : undefined}
                                                    bg={immutable ? "surface-subtle" : invalid ? "red.50" : "surface"}
                                                    _dark={invalid && !immutable ? {bg: "rgba(245, 101, 101, 0.15)"} : undefined}
                                                    onChange={(e) => updateHour(row.key, day, e.target.value)}
                                                />
                                            </Td>
                                        );
                                    })}
                                    <Td textAlign="center">
                                        {rowShowsTrash(row, mode) && (
                                            <IconButton
                                                aria-label={row.deleted ? "Undo deletion" : "Delete row"}
                                                icon={row.deleted ? <FaUndo/> : <FaTrash/>}
                                                size="xs"
                                                colorScheme={row.deleted ? "gray" : "red"}
                                                variant="outline"
                                                onClick={() => toggleDeleteRow(row.key)}
                                            />
                                        )}
                                    </Td>
                                </Tr>
                            );
                        })}
                        <Tr bg="surface-muted" fontWeight={700} borderTop="2px solid" borderTopColor="border-subtle">
                            <Td>Total</Td>
                            <Td>Daily Sum</Td>
                            <Td/>
                            {totals.daySums.map((sum, day) => (
                                <Td key={day} textAlign="center">{sum > 0 ? `${formatHourString(sum)}h` : ""}</Td>
                            ))}
                            <Td/>
                        </Tr>
                    </Tbody>
                </Table>
            </TableContainer>

            <Text mt={2} fontWeight={700}>Week Total: {formatHourString(totals.weekSum)}h</Text>
            {totals.invalidCount > 0 && (
                <Text fontSize="xs" color="red.600" _dark={{color: "red.300"}}>
                    Some cells are invalid. Use empty or numbers from 0 to 24 with up to {decimals} decimal{decimals === 1 ? "" : "s"}.
                </Text>
            )}
        </Box>
    );
}
